import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Sparkles, CheckCircle2, Phone, Loader2 } from 'lucide-react';
import { utmPayload, getPromoVariant, trackPromo } from '../lib/tracking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIP_SAVE_URL = 'https://squareup.com/customer-programs/enroll/JaMWYSYKP3kw?utm_medium=copied-link&utm_source=online';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// A/B copy variants for the Pour-to-Reveal popup. Visitor gets a sticky 50/50 assignment.
// A = standard 10% off · B = limited-time 20% off for first-time clients.
const VARIANTS = {
  A: {
    percent: 10,
    eyebrow: '10% OFF',
    headline: 'Welcome to The Lokal!',
    subcopy: '10% off your first wine kit',
    cta: 'Claim my 10% off',
  },
  B: {
    percent: 20,
    eyebrow: '20% OFF · LIMITED TIME',
    headline: '20% off for first-time clients',
    subcopy: 'New here? Take 20% off your first wine kit — limited time only',
    cta: 'Claim my 20% off',
  },
};

const isSuppressedPath = () =>
  window.location.pathname.startsWith('/admin') ||
  window.location.pathname.startsWith('/wine-of-the-year');

// Pour-to-reveal keyframes. Honors prefers-reduced-motion (fill + content appear instantly).
const POUR_STYLES = `
@keyframes lokalWinePour { 0% { transform: translateY(118px); } 100% { transform: translateY(30px); } }
@keyframes lokalReveal { 0% { opacity: 0; transform: translateY(14px); } 100% { opacity: 1; transform: translateY(0); } }
@keyframes lokalShimmer { 0%,100% { opacity: .25; } 50% { opacity: .6; } }
.lokal-wine-fill { animation: lokalWinePour 1.35s cubic-bezier(.22,.61,.36,1) forwards; }
.lokal-wine-surface { animation: lokalShimmer 2.2s ease-in-out .9s infinite; }
.lokal-reveal { opacity: 0; animation: lokalReveal .8s ease-out .55s forwards; }
@media (prefers-reduced-motion: reduce) {
  .lokal-wine-fill { animation: none; transform: translateY(30px); }
  .lokal-wine-surface { animation: none; opacity: .5; }
  .lokal-reveal { animation: none; opacity: 1; transform: none; }
}
`;

const WineGlass = () => (
  <svg viewBox="0 0 120 210" className="w-28 h-auto sm:w-32" role="img" aria-label="A glass of red wine filling">
    <defs>
      <linearGradient id="lokalWineGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#8e1f2b" />
        <stop offset="55%" stopColor="#6b1420" />
        <stop offset="100%" stopColor="#4a0d16" />
      </linearGradient>
      <clipPath id="lokalBowlClip">
        <path d="M32 16 C32 70 44 112 60 118 C76 112 88 70 88 16 Z" />
      </clipPath>
    </defs>
    <path d="M30 14 C30 72 42 116 60 122 C78 116 90 72 90 14 Z" fill="#ffffff" fillOpacity="0.06" stroke="#f4e3c0" strokeWidth="1.6" strokeOpacity="0.85" />
    <g clipPath="url(#lokalBowlClip)">
      <g className="lokal-wine-fill">
        <rect x="0" y="0" width="120" height="210" fill="url(#lokalWineGrad)" />
        <ellipse className="lokal-wine-surface" cx="60" cy="0" rx="46" ry="3.4" fill="#d16b74" />
      </g>
    </g>
    <path d="M38 22 C39 60 46 96 56 112" fill="none" stroke="#ffffff" strokeOpacity="0.28" strokeWidth="2.2" strokeLinecap="round" />
    <rect x="58" y="122" width="4" height="56" rx="2" fill="#f4e3c0" fillOpacity="0.85" />
    <path d="M38 192 C38 183 82 183 82 192 C82 196 38 196 38 192 Z" fill="#f4e3c0" fillOpacity="0.85" />
  </svg>
);

const PopupContent = ({ onClose, prefix, onBook, copy, variant, trigger }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Fire a single A/B "view" when this popup actually opens (DialogContent mounts on open).
  useEffect(() => {
    trackPromo(variant, 'view', trigger);
  }, [variant, trigger]);

  const claim = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const local = (value.split('@')[0] || '').replace(/[^a-zA-Z0-9]/g, '');
      await axios.post(`${API}/newsletter/subscribe`, {
        firstName: local.length >= 2 ? local.slice(0, 50) : 'Friend',
        lastName: 'VIP',
        email: value,
        ...utmPayload(),
      });
      trackPromo(variant, 'submit', trigger);
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 409) {
        trackPromo(variant, 'submit', trigger);
        setSubmitted(true); // already on the list — still a win, show success
      } else {
        setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-md p-0 border-0 bg-[#2a0d12] max-h-[90vh] overflow-hidden flex flex-col" data-testid={`promo-popup-${prefix}`}>
      <style>{POUR_STYLES}</style>
      <button
        onClick={onClose}
        data-testid={`${prefix}-close`}
        className="absolute right-3 top-3 rounded-full bg-white/10 p-2 text-white/90 transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-[#FDB913] z-20"
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </button>

      <div className="overflow-y-auto">
      {/* Stage: wine glass against deep wine backdrop */}
      <div className="relative flex flex-col items-center pt-8 pb-5 px-6 text-center"
        style={{ background: 'radial-gradient(130% 80% at 50% 0%, #5a141d 0%, #2a0d12 70%)' }}>
        <WineGlass />
        <div className="lokal-reveal mt-3" data-testid={`${prefix}-variant-${variant}`}>
          {trigger === 'exit' && (
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#e7d6bf]/80 mb-1">Before you go —</p>
          )}
          <div className="inline-flex items-center gap-2 text-[#FDB913] font-bold tracking-wide">
            <Sparkles className="w-4 h-4" />
            <span className="text-lg">{copy.eyebrow}</span>
            <Sparkles className="w-4 h-4" />
          </div>
          <h2 className="mt-1 text-2xl font-bold text-[#F5EBDC]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {copy.headline}
          </h2>
          <p className="mt-1 text-sm text-[#e7d6bf]">{copy.subcopy}</p>
        </div>
      </div>

      {/* Action */}
      <div className="bg-[#fdfbf5] px-6 py-5 lokal-reveal">
        {submitted ? (
          <div className="text-center" data-testid={`${prefix}-success`}>
            <CheckCircle2 className="w-12 h-12 mx-auto mb-2" style={{ color: '#722F37' }} />
            <h3 className="text-xl font-bold" style={{ color: '#722F37', fontFamily: "'Playfair Display', Georgia, serif" }}>
              You're in — your {copy.percent}% is reserved
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              Welcome to the VIP list! Your {copy.percent}% off is honored on your first wine kit when you book a consultation or join Sip &amp; Save — just mention your email.
            </p>
            <Button onClick={onBook} data-testid={`${prefix}-book-success`}
              className="mt-4 w-full bg-[#722F37] hover:bg-[#5c252c] text-white font-semibold py-5">
              <Phone className="w-4 h-4 mr-2" /> Book a free consultation
            </Button>
          </div>
        ) : (
          <form onSubmit={claim} noValidate className="space-y-3">
            <p className="text-center text-sm text-gray-600">
              Enter your email and we'll reserve your discount — no code to remember.
            </p>
            <div>
              <Input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                placeholder="you@example.com"
                data-testid={`${prefix}-email`}
                aria-label="Email address"
                className="h-12 text-base bg-white border-[#e3d4b4] focus-visible:ring-[#FDB913]"
              />
              {error && <p className="mt-1 text-xs text-red-600" data-testid={`${prefix}-error`}>{error}</p>}
            </div>
            <Button type="submit" disabled={submitting} data-testid={`${prefix}-claim`}
              className="w-full bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 font-bold py-6 text-base">
              {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Reserving…</> : copy.cta}
            </Button>
            <Button type="button" onClick={onBook} variant="outline" data-testid={`${prefix}-book`}
              className="w-full border-2 border-[#722F37] text-[#722F37] hover:bg-[#722F37]/10 font-semibold py-5">
              <Phone className="w-4 h-4 mr-2" /> Book a free consultation
            </Button>
            <p className="text-[11px] text-gray-500 text-center pt-1">
              Offer tied to our{' '}
              <a href={SIP_SAVE_URL} target="_blank" rel="noopener noreferrer" data-testid={`${prefix}-sipsave`}
                className="underline font-medium text-[#722F37] hover:text-[#5c252c]">Sip &amp; Save</a>{' '}
              program. We'll email you winery news; unsubscribe anytime.
            </p>
          </form>
        )}
      </div>
      </div>
    </DialogContent>
  );
};

const PromoPopup = ({ onBookCall }) => {
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasSeenExit, setHasSeenExit] = useState(false);
  const [variant] = useState(getPromoVariant);
  const copy = VARIANTS[variant];

  // Welcome popup — once per session, a few seconds after load, never on admin/voting pages.
  useEffect(() => {
    if (isSuppressedPath() || sessionStorage.getItem('lokal_welcome_seen')) return undefined;
    const t = setTimeout(() => {
      if (isSuppressedPath()) return; // re-check at fire time (SPA may have navigated)
      setShowWelcomePopup(true);
      sessionStorage.setItem('lokal_welcome_seen', '1');
    }, 8000);
    return () => clearTimeout(t);
  }, []);

  // Exit-intent — mouse leaves toward the top of the viewport.
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasSeenExit && !isSuppressedPath() && !sessionStorage.getItem('lokal_exit_seen')) {
        setShowExitPopup(true);
        setHasSeenExit(true);
        sessionStorage.setItem('lokal_exit_seen', '1');
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasSeenExit]);

  const handleBookNow = () => {
    setShowWelcomePopup(false);
    setShowExitPopup(false);
    onBookCall();
  };

  return (
    <>
      <Dialog open={showWelcomePopup} onOpenChange={setShowWelcomePopup}>
        <PopupContent onClose={() => setShowWelcomePopup(false)} prefix="welcome" onBook={handleBookNow} copy={copy} variant={variant} trigger="welcome" />
      </Dialog>

      <Dialog open={showExitPopup} onOpenChange={setShowExitPopup}>
        <PopupContent onClose={() => setShowExitPopup(false)} prefix="exit" onBook={handleBookNow} copy={copy} variant={variant} trigger="exit" />
      </Dialog>
    </>
  );
};

export default PromoPopup;
